import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';

const ProjectContext = createContext();

const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error(`useProjectContext must be used within a ProjectContext`);
  }
  return context;
};

const ProjectContextProvider = ({ OrgID, ProjectID, memberMode = false, children }) => {
  const org_id = OrgID;
  const project_id = ProjectID;
  const [project, setProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [english_transcripts, setEnglishTranscripts] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedElevenLabsApi, setSelectedElevenLabsApi] = useState('multilingual_v2');

  // Media playback state
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [mediaDuration, setMediaDuration] = useState(0);
  const mediaRef = useRef(null);

  useEffect(() => {
    fetchProject();
    fetchVoices();
    const storedApi = localStorage.getItem('eleven_labs_api');
    if (storedApi === 'multilingual_v2' || storedApi === 'eleven_v3') {
      setSelectedElevenLabsApi(storedApi);
    }
  }, [org_id, project_id]);

  const handleElevenLabsApiChange = (api) => {
    setSelectedElevenLabsApi(api);
    localStorage.setItem('eleven_labs_api', api);
  };

  const fetchVoices = async () => {
    try {
      const response = await fetch('/api_super_admin/voices');
      if (!response.ok) throw new Error('Failed to fetch voices');
      const data = await response.json();
      setVoices(data);
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  };

  // Compute active transcript ID based on current playback time
  const activeTranscriptId = useMemo(() => {
    const currentTranscripts = english_transcripts.length > 0 ? english_transcripts : transcripts;
    const active = currentTranscripts.find(t => 
      currentPlaybackTime >= parseFloat(t.start_time) && 
      currentPlaybackTime < parseFloat(t.end_time)
    );
    return active?.id || null;
  }, [currentPlaybackTime, transcripts, english_transcripts]);

  // Media control functions
  const handleTimeUpdate = useCallback((time) => {
    setCurrentPlaybackTime(time);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (mediaRef.current) {
      if (isPlaying) {
        mediaRef.current.pause();
      } else {
        mediaRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const handleSeek = useCallback((time) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = time;
      setCurrentPlaybackTime(time);
    }
  }, []);

  const seekToTranscript = useCallback((transcript) => {
    if (transcript?.start_time != null) {
      handleSeek(parseFloat(transcript.start_time));
    }
  }, [handleSeek]);

  const toggleAutoScroll = useCallback(() => {
    setAutoScrollEnabled(prev => !prev);
  }, []);

  const fetchProject = async () => {
    setLoading(true);
    const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}`);
    const data = await response.json();
    console.log("data", data);
    setProject(data);
    setLoading(false);
  }

  const updateProjectStatus = async (projectId, newStatus) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project: {
            project_status: newStatus
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update project status');
      }

      fetchProject(projectId);
    } catch (error) {
      console.error("Error updating project status:", error);
    }
  };

  const saveVideoUrlToProject = async (videoUrl) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_video_url`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ video_url: videoUrl }),
      });

      if (!response.ok) {
        throw new Error('Failed to save video URL to project');
      }
      
      await fetchProject();
      return true;
    } catch (error) {
      console.error('Error saving video URL:', error);
      throw error;
    }
  };

  const saveAudioUrlToProject = async (audioUrl) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_audio_url`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ audio_url: audioUrl }),
      });

      if (!response.ok) {
        throw new Error('Failed to save audio URL to project');
      }
      
      await fetchProject();
      return true;
    } catch (error) {
      console.error('Error saving audio URL:', error);
      throw error;
    }
  };

  const updateProject = async (updatedProject) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ project: updatedProject }),
      });

      if (!response.ok) {
        throw new Error('Failed to update project');
      }

      await fetchProject();
      return true;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  const uploadSrtFile = async (formData) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/upload_srt`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload SRT file');
      }

      await updateProject({
        ...project,
        project_status: "fix_transcript",
        has_transcript: true
      });
      
      return true;
    } catch (error) {
      console.error('Error uploading SRT:', error);
      throw error;
    }
  };

  const fetchTranscripts = async () => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/transcripts`);
      const data = await response.json();
      setTranscripts(data);
    } catch (error) {
      console.error('Error fetching transcripts:', error);
    }
  };

  const fetchEnglishTranscripts = async () => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/english_transcripts`);
      if (!response.ok) throw new Error('Failed to fetch English transcripts');
      const data = await response.json();
      setEnglishTranscripts(data);
    } catch (error) {
      console.error('Error fetching English transcripts:', error);
    }
  };

  const handleMarkTranscriptAsError = async (transcriptId, hasError) => {
    try {
      await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/mark_transcript_as_error`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript_id: transcriptId,
          has_error: hasError
        })
      });
      return true;
    } catch (error) {
      console.error('Failed to mark/unmark transcript as error:', error);
      return false;
    }
  };

  const handleMarkTranscriptAsResolved = async (transcriptId, hasErrorResolved) => {
    try {
      await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/mark_transcript_as_resolved`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript_id: transcriptId,
          has_error_resolved: hasErrorResolved
        })
      });
      return true;
    } catch (error) {
      console.error('Failed to mark/unmark transcript as resolved:', error);
      return false;
    }
  };

  const handleSaveTranscript = async (transcriptId, content) => {
    try {
      await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_transcript`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript_id: transcriptId,
          content: content
        })
      });
      return true;
    } catch (error) {
      console.error('Failed to save transcript:', error);
      return false;
    }
  };

  // Insert new transcript row
  const insertTranscript = async (startTime, endTime, text = '') => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/insert_transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          start_time: startTime,
          end_time: endTime,
          text: text
        })
      });
      
      if (!response.ok) throw new Error('Failed to insert transcript');
      const data = await response.json();
      
      if (data.success) {
        setTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error inserting transcript:', error);
      throw error;
    }
  };

  // Update transcript timecodes
  const updateTranscriptTimecodes = async (transcriptId, startTime, endTime) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_transcript_timecodes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          transcript_id: transcriptId,
          start_time: startTime,
          end_time: endTime
        })
      });
      
      if (!response.ok) throw new Error('Failed to update transcript timecodes');
      const data = await response.json();
      
      if (data.success) {
        setTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error updating transcript timecodes:', error);
      throw error;
    }
  };

  // Delete transcript
  const deleteTranscript = async (transcriptId) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/delete_transcript`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          transcript_id: transcriptId
        })
      });
      
      if (!response.ok) throw new Error('Failed to delete transcript');
      const data = await response.json();
      
      if (data.success) {
        setTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error deleting transcript:', error);
      throw error;
    }
  };

  // Insert new english transcript row
  const insertEnglishTranscript = async (startTime, endTime, originalText = '', text = '') => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/insert_english_transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          start_time: startTime,
          end_time: endTime,
          original_text: originalText,
          text: text
        })
      });
      
      if (!response.ok) throw new Error('Failed to insert english transcript');
      const data = await response.json();
      
      if (data.success) {
        setEnglishTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error inserting english transcript:', error);
      throw error;
    }
  };

  // Split english transcript row at a time + text boundary
  const splitEnglishTranscript = async (transcriptId, splitTime, firstText, secondText) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/split_english_transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          english_transcript_id: transcriptId,
          split_time: splitTime,
          first_text: firstText,
          second_text: secondText
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to split english transcript');
      }

      setEnglishTranscripts(data.transcripts);
      return data;
    } catch (error) {
      console.error('Error splitting english transcript:', error);
      throw error;
    }
  };

  // Update english transcript timecodes
  const updateEnglishTranscriptTimecodes = async (transcriptId, startTime, endTime) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_english_transcript_timecodes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          english_transcript_id: transcriptId,
          start_time: startTime,
          end_time: endTime
        })
      });
      
      if (!response.ok) throw new Error('Failed to update english transcript timecodes');
      const data = await response.json();
      
      if (data.success) {
        setEnglishTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error updating english transcript timecodes:', error);
      throw error;
    }
  };

  // Delete english transcript
  const deleteEnglishTranscript = async (transcriptId) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/delete_english_transcript`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          english_transcript_id: transcriptId
        })
      });
      
      if (!response.ok) throw new Error('Failed to delete english transcript');
      const data = await response.json();
      
      if (data.success) {
        setEnglishTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error deleting english transcript:', error);
      throw error;
    }
  };

  const removeDuplicateEnglishTranscripts = async () => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/remove_duplicate_english_transcripts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
      });

      if (!response.ok) throw new Error('Failed to remove duplicate english transcripts');
      const data = await response.json();

      if (data.success) {
        setEnglishTranscripts(data.transcripts);
      }
      return data;
    } catch (error) {
      console.error('Error removing duplicate english transcripts:', error);
      throw error;
    }
  };

  const handleSaveSentenceTranscript = async (transcriptId, content) => {
    try {
      await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_sentence_transcript`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          english_transcript_id: transcriptId,
          content: content
        })
      });
      return true;
    } catch (error) {
      console.error('Failed to save transcript:', error);
      return false;
    }
  };

  const handleSaveCorrectEnglishSentenceTranscript = async (transcriptId, content) => {
    try {
      await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/update_correct_english_sentence_transcript`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          english_transcript_id: transcriptId,
          content: content
        })
      });
      return true;
    } catch (error) {
      console.error('Failed to save transcript:', error);
      return false;
    }
  };

  const mergeEnglishTranscripts = async (transcriptIds, mergedTranscripts) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/merge_english_transcript`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript_ids: transcriptIds,
          merged_transcripts: mergedTranscripts
        })
      });
      
      if (!response.ok) throw new Error('Failed to merge transcripts');
      return await response.json();
    } catch (error) {
      console.error('Error merging transcripts:', error);
      throw error;
    }
  };

  const translateSingleTranscript = async (transcript, added_prompt = '') => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/translate_single_transcript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          english_transcript_id: transcript.id,
          text: transcript.original_text,
          source_lang: 'auto',
          target_lang: 'en',
          context: {
            pre_sentence: transcript.pre_sentence,
            post_sentence: transcript.post_sentence
          },
          added_prompt: added_prompt
        })
      });
      
      const resp = await response.json();

      if (resp.success) {
        const english_transcript = english_transcripts.find(et => et.id === transcript.id);
        english_transcript.text = resp.translated_text;
        setEnglishTranscripts([...english_transcripts, english_transcript]);
      }
      
      return true;
    } catch (error) {
      console.error('Translation error:', error);
      throw error;
    }
  };

  const generateEnglishTTS = async (voiceId = null) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_english_tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          voice_id: voiceId || selectedVoiceId || null,
          eleven_labs_api: selectedElevenLabsApi
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate TTS');
      }
      
      await fetchEnglishTranscripts();
      return true;
    } catch (error) {
      console.error('Error generating TTS:', error);
      throw error;
    }
  };

  const generateSingleEnglishTTS = async (englishTranscriptId, voiceId = null) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_single_english_tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          english_transcript_id: englishTranscriptId,
          voice_id: voiceId || selectedVoiceId || null,
          eleven_labs_api: selectedElevenLabsApi
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate TTS');
      }
      
      const result = await response.json();
      
      // Update local state with the new transcript data
      if (result.success && result.transcript) {
        setEnglishTranscripts(prev => 
          prev.map(t => t.id === englishTranscriptId ? { ...t, ...result.transcript } : t)
        );
      }
      
      return result;
    } catch (error) {
      console.error('Error generating single TTS:', error);
      throw error;
    }
  };

  const translateAllTranscripts = async (added_prompt = '') => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/translate_all_transcripts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          english_transcript_ids: english_transcripts.map(et => et.id),
          added_prompt: added_prompt
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to translate all transcripts');
      }
      
      return true;
    } catch (error) {
      console.error('Error translating all transcripts:', error);
      throw error;
    }
  };

  const fetchAvailableTranslations = async () => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/available_translations`);
      if (!response.ok) throw new Error('Failed to fetch available translations');
      return await response.json();
    } catch (error) {
      console.error('Error fetching available translations:', error);
      throw error;
    }
  };

  const generateTranslations = async (languageCode, options = {}) => {
    const {
      translation_provider = 'openai',
      force = false,
      added_prompt = ''
    } = options;

    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          language_code: languageCode,
          translation_provider,
          force,
          added_prompt: translation_provider === 'openai' ? added_prompt : ''
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate translations');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error generating translations:', error);
      throw error;
    }
  };

  const generateTranslationTTS = async (languageCode, voiceId = null) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/generate_translation_tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          language_code: languageCode,
          voice_id: voiceId || selectedVoiceId || null,
          eleven_labs_api: selectedElevenLabsApi
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate translation TTS');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error generating translation TTS:', error);
      throw error;
    }
  };

  const fetchTranslationProgress = async (languageCode) => {
    try {
      const response = await fetch(`/api_super_admin/orgs/${org_id}/projects/${project_id}/translation_progress?language_code=${languageCode}`);
      if (!response.ok) throw new Error('Failed to fetch translation progress');
      const data = await response.json();
      return data.progress;
    } catch (error) {
      console.error('Error fetching translation progress:', error);
      throw error;
    }
  };

  // Helper function to format seconds to SRT timecode format
  const formatTimeCode = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '00:00:00,000';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
  };

  // Helper function to parse SRT timecode to seconds
  const parseTimeCode = (timeCode) => {
    if (!timeCode) return 0;
    const match = timeCode.match(/(\d{2}):(\d{2}):(\d{2})[,.](\d{3})/);
    if (!match) return 0;
    const [, hrs, mins, secs, ms] = match;
    return parseInt(hrs) * 3600 + parseInt(mins) * 60 + parseInt(secs) + parseInt(ms) / 1000;
  };
   
  const contextValue = {
    org_id,
    project_id,
    memberMode,
    project,
    loading,
    transcripts,
    english_transcripts,
    // Voices
    voices,
    selectedVoiceId,
    setSelectedVoiceId,
    fetchVoices,
    selectedElevenLabsApi,
    setSelectedElevenLabsApi,
    handleElevenLabsApiChange,
    // Media playback
    currentPlaybackTime,
    isPlaying,
    activeTranscriptId,
    autoScrollEnabled,
    mediaDuration,
    mediaRef,
    handleTimeUpdate,
    handlePlayPause,
    handleSeek,
    seekToTranscript,
    toggleAutoScroll,
    setIsPlaying,
    setMediaDuration,
    // Project methods
    fetchProject,
    fetchTranscripts,
    fetchEnglishTranscripts,
    updateProject,
    updateProjectStatus,
    saveVideoUrlToProject,
    saveAudioUrlToProject,
    uploadSrtFile,
    // Transcript methods
    handleMarkTranscriptAsError,
    handleMarkTranscriptAsResolved,
    handleSaveTranscript,
    insertTranscript,
    updateTranscriptTimecodes,
    deleteTranscript,
    // English transcript methods
    handleSaveSentenceTranscript,
    handleSaveCorrectEnglishSentenceTranscript,
    setEnglishTranscripts,
    mergeEnglishTranscripts,
    insertEnglishTranscript,
    splitEnglishTranscript,
    updateEnglishTranscriptTimecodes,
    deleteEnglishTranscript,
    removeDuplicateEnglishTranscripts,
    // Translation methods
    translateSingleTranscript,
    generateEnglishTTS,
    generateSingleEnglishTTS,
    translateAllTranscripts,
    fetchAvailableTranslations,
    generateTranslations,
    generateTranslationTTS,
    fetchTranslationProgress,
    // Utility methods
    formatTimeCode,
    parseTimeCode
  };

  return <ProjectContext.Provider value={contextValue}>{children}</ProjectContext.Provider>;
};

export { ProjectContext, ProjectContextProvider, useProjectContext };
